import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { formatDateShort } from "@/shared/lib/format-date";
import { i18n } from "@/shared/lib/i18n";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useAccruals } from "./use-accruals";
import { FinancePagination } from "./FinancePagination";
import { formatMoney, moneyFromCentsString } from "./money";
import { ACCRUALS_PAGE_SIZE } from "./constants";
import type { Accrual } from "./types";

const LABEL = "font-sans text-sm font-medium text-foreground";

type AccrualStatus = "settled" | "liquidated" | "pending";

function accrualStatus(accrual: Accrual): AccrualStatus {
  if (accrual.liquidationLedgerEntryId) return "liquidated";
  if (accrual.settledAt) return "settled";
  return "pending";
}

const STATUS_CLASS: Readonly<Record<AccrualStatus, string>> = {
  settled: "bg-emerald-500/10 text-emerald-600",
  liquidated: "bg-muted text-muted-foreground",
  pending: "bg-amber-500/10 text-amber-600",
};

export function AccrualsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Seeded ONCE from the deep link (?venueId=), then owned by local state — the
  // URL is not kept in sync as the operator types (this is a drill-down filter,
  // not a shareable "current search"). If a same-page venue-switcher ever links
  // between different venueId values without a route change, this would need to
  // become a `key`-remounted or effect-synced pattern instead.
  const [venueId, setVenueId] = useState(() => searchParams.get("venueId") ?? "");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [page, setPage] = useState(1);

  const debouncedVenueId = useDebouncedValue(venueId, 300);
  const debouncedSubscriptionId = useDebouncedValue(subscriptionId, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedVenueId, debouncedSubscriptionId]);

  const { data, isLoading, isFetching, isError } = useAccruals({
    venueId: debouncedVenueId,
    subscriptionId: debouncedSubscriptionId,
    page,
  });
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasFilters = venueId.trim() !== "" || subscriptionId.trim() !== "";

  const handleReset = () => {
    setVenueId("");
    setSubscriptionId("");
  };

  return (
    <div data-testid="accruals-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("finance.accruals.eyebrow")}</p>
        <h1 className="text-3xl">{t("finance.accruals.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("finance.accruals.subtitle")}
        </p>
      </header>

      <div data-testid="accruals-filters" className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label htmlFor="accrual-venue" className={LABEL}>
            {t("finance.accruals.filters.venue")}
          </label>
          <Input
            id="accrual-venue"
            data-testid="filter-accrual-venue"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="accrual-subscription" className={LABEL}>
            {t("finance.accruals.filters.subscription")}
          </label>
          <Input
            id="accrual-subscription"
            data-testid="filter-accrual-subscription"
            value={subscriptionId}
            onChange={(e) => setSubscriptionId(e.target.value)}
            autoComplete="off"
          />
        </div>
        {hasFilters && (
          <Button data-testid="accruals-filters-reset" variant="outline" onClick={handleReset}>
            {t("finance.accruals.filters.reset")}
          </Button>
        )}
      </div>

      {isLoading && (
        <p data-testid="accruals-loading" className="font-sans text-sm text-muted-foreground">
          {t("finance.accruals.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="accruals-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("finance.accruals.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div
          data-testid="accruals-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {hasFilters ? t("finance.accruals.emptyFiltered") : t("finance.accruals.empty")}
          </p>
        </div>
      )}

      {!isError && items.length > 0 && (
        <div
          aria-busy={isFetching}
          className={isFetching ? "space-y-4 opacity-70 transition-opacity" : "space-y-4"}
        >
          <ul data-testid="accruals-list" className="space-y-2">
            {items.map((accrual) => (
              <AccrualRow key={accrual.id} accrual={accrual} />
            ))}
          </ul>
          <FinancePagination
            page={page}
            limit={ACCRUALS_PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setPage}
            testIdPrefix="accruals"
          />
        </div>
      )}
    </div>
  );
}

function AccrualRow({ accrual }: { readonly accrual: Accrual }) {
  const { t } = useTranslation();
  const status = accrualStatus(accrual);
  return (
    <li
      data-testid={`accrual-row-${accrual.id}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="min-w-0 space-y-0.5">
        <p className="font-mono text-sm font-medium text-foreground">
          {formatMoney(moneyFromCentsString(accrual.totalCents, accrual.currency))}{" "}
          <span className="uppercase text-muted-foreground">{accrual.currency}</span>
        </p>
        <p className="font-sans text-xs text-muted-foreground">
          {t("finance.accruals.period", {
            from: formatDateShort(accrual.periodStart, i18n.language),
            to: formatDateShort(accrual.periodEnd, i18n.language),
          })}
          {" · "}
          {t("finance.accruals.checkins", {
            venue: accrual.venueCheckinCount,
            pool: accrual.totalCheckinsInPool,
          })}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 text-right">
        <span
          data-testid={`accrual-status-${accrual.id}`}
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status]}`}
        >
          {t(`finance.accruals.status.${status}`)}
        </span>
        {status === "liquidated" && accrual.liquidationReason && (
          <p
            data-testid={`accrual-liquidation-reason-${accrual.id}`}
            className="max-w-xs font-sans text-xs text-muted-foreground"
          >
            {t("finance.accruals.liquidationReason", {
              reason: accrual.liquidationReason,
            })}
          </p>
        )}
      </div>
    </li>
  );
}
